/**
 * Общие детали экрана «Хронум»: контейнер, заголовки разделов, пустые
 * состояния, базовая таблица. Шкала шрифта и метки — общие с экранами техники
 * и ПРЦ Экономиста (`@entities/fleet`), плашки и вкладки — оттуда же.
 */
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { riseIn } from '@shared/theme/animations';
import { fleetLeading, fleetType } from '@entities/fleet';
import type { ChronumTone } from '@entities/chronum';
import type { NoticeTone } from '@widgets/FleetRegistry/ui';

/** Ширина контейнера, ниже которой таблицы становятся карточками. */
export const NARROW = '640px';

/** Цвет тона: точки, полосы и кромки. Текст рядом всегда основного цвета. */
export function toneColor(theme: DefaultTheme, tone: ChronumTone): string {
  switch (tone) {
    case 'success':
      return theme.colors.status.success;
    case 'warning':
      return theme.colors.status.warning;
    case 'danger':
      return theme.colors.status.danger;
    case 'info':
      return theme.colors.status.info;
    case 'neutral':
      return theme.colors.text.muted;
    case 'muted':
      return theme.colors.border;
  }
}

/** Тон плашки: нейтральное и «нет данных» — как сведения. */
export function noticeTone(tone: ChronumTone): NoticeTone {
  return tone === 'success' || tone === 'warning' || tone === 'danger' ? tone : 'info';
}

/** Точка тона перед словом итога. */
export const Dot = styled.span<{ $tone: ChronumTone }>`
  flex: none;
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme, $tone }) => toneColor(theme, $tone)};
`;

/**
 * Корень экрана — контейнер для container queries. `color-scheme` по теме
 * приложения: выпадающие списки и флажки рисуются в тон странице, а не по
 * теме Windows.
 */
export const Root = styled.div`
  container: chronum / inline-size;
  color-scheme: ${({ theme }) => (theme.name === 'dark' ? 'dark' : 'light')};
  animation: ${riseIn} 320ms ${({ theme }) => theme.motion.easeOut} both;
`;

/** Раздел внутри вкладки. */
export const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

export const SectionHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: ${fleetType.lead};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  letter-spacing: -0.01em;
  text-wrap: balance;
`;

/** Пояснение рядом с заголовком или под ним. */
export const SectionNote = styled.p`
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

/** Подпись-эйбрау: «ЧТО ПРИХОДИТ ИЗ ХРОНУМА». */
export const Eyebrow = styled.p`
  margin: 0;
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/**
 * Ссылка в ячейке таблицы: текст основного цвета, но подчёркнут — видно, что
 * это переход (группа механизаторов, стройка у Руководителя).
 */
export const cellLink = css`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colors.borderStrong};
  text-underline-offset: 3px;
  overflow-wrap: anywhere;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    text-decoration-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration-color: currentColor;
  }
`;

/** Ссылка-переход со стрелкой: «Группы механизаторов →». */
export const ArrowLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-underline-offset: 3px;

  &:hover {
    text-decoration: underline;
  }

  & > svg {
    flex: none;
  }
`;

/** Пустое состояние раздела: что случилось и что сделать. */
export const EmptyBox = styled.div`
  max-width: 680px;
  padding: ${({ theme }) => theme.spacing(6)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};

  & > ${Eyebrow} {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.micro};
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > h3 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.lead};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    text-wrap: balance;
  }

  & > p {
    margin: 0;
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > p + p,
  & > .actions {
    margin-top: ${({ theme }) => theme.spacing(4)};
  }

  @container chronum (max-width: ${NARROW}) {
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

/**
 * Таблица экрана: подписи колонок мелкими прописными, тонкие линейки, числа
 * справа моноширинными цифрами. Узкую раскладку каждая таблица задаёт сама.
 */
export const DataTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  thead th {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    line-height: ${fleetLeading.tight};
    text-transform: uppercase;
    text-align: left;
    text-wrap: balance;
    color: ${({ theme }) => theme.colors.text.secondary};
    vertical-align: bottom;
  }

  th.num,
  td.num {
    text-align: right;
  }

  tbody td,
  tbody th,
  tfoot td,
  tfoot th {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    text-align: left;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  td.num {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.empty {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  tfoot td,
  tfoot th {
    border-bottom: none;
    border-top: 1px solid ${({ theme }) => theme.colors.borderStrong};
    font-weight: 600;
  }

  .narrow-only {
    display: none;
  }
`;

/** Мелкая строка под основным текстом ячейки. */
export const CellMeta = styled.span`
  display: block;
  margin-top: 2px;
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
  text-wrap: pretty;
`;

/** Скелетон списка: строки разной длины. */
export function ListSkeleton({ rows = 6, label }: { rows?: number; label: string }) {
  return (
    <div aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonLine key={i} $height={18} $width={i % 3 === 0 ? '42%' : '100%'} />
      ))}
    </div>
  );
}
